import { Dispatch } from 'react-redux';
import { Action } from '../../actions';
import { DriveItemListDisplayType } from './constants';
import { Api } from '../../typings/api';
import { StoreState } from '../../reducers';
import { getDriveItemStatusFromMap, DriveItemFetchState, DriveListState, FetchStatus } from './reducer';
import { requestMobileAppDownload } from '../DownloadManager/actions';
import { DownloadMode } from '../DownloadManager/reducer';
import { RouterParamsState } from '../SetRouteParams/reducer';
import NativeApi from '../../utils/nativeApi';
import UrlUtils from '../../utils/url';
import UserAgentUtils from '../../utils/userAgent';
import DriveItemUtils from '../../utils/driveItem';
import Utils from '../../utils';
import ApiUtils from '../../utils/api';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';
import { createNotification } from '../NotificationBar/actions';
import RoutesUtils from '../../utils/routes';
import LocalStorageManager from '../../utils/localStorage/manager';
import DownloadManagerUtils from '../../utils/downloadManager';
import { AppType } from '../App/reducer';

export enum DriveItemListActionKeys {
	REQUEST_DRIVE_META = '@@driveList/REQUEST_DRIVE_META',
	RECEIVE_DRIVE_META = '@@driveList/RECEIVE_DRIVE_META',
	FETCH_ERROR_DRIVE_META = '@@driveList/FETCH_ERROR_DRIVE_META',
	REQUEST_CONTENT_BLOCK = '@@driveList/REQUEST_CONTENT_BLOCK',
	RECEIVE_CONTENT_BLOCK = '@@driveList/RECEIVE_CONTENT_BLOCK',
	FETCH_ERROR_CONTENT_BLOCK = '@@driveList/FETCH_ERROR_CONTENT_BLOCK',
	RECEIVE_DRIVE_ITEMS = '@@driveList/RECEIVE_DRIVE_ITEMS',
	RECEIVE_RESOLUTIONS = '@@driveList/RECEIVE_RESOLUTIONS',
	UPDATE_DRIVE_ITEM_LIST_DISPLAY_TYPE = '@@driveList/UPDATE_DRIVE_ITEM_LIST_DISPLAY_TYPE',
	REQUEST_ALL = '@@driveList/REQUEST_ALL',
	RECEIVE_ALL = '@@driveList/RECEIVE_ALL'
}

interface RequestDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.REQUEST_DRIVE_META;
	driveItemId: number;
}

interface ReceiveDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META;
	driveItemId: number;
}

interface FetchErrorDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META;
	driveItemId: number;
}

interface RequestContentBlockAction extends Action {
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveContentBlockAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveDriveItemsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS;
	driveItems: { [k: number]: Api.DriveItem };
	isInGallery?: boolean;
	multiAppLogin?: string;
}

interface FetchErrorContentBlockAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveResolutionsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS;
	resolutions: { [k: string]: Api.ResolutionGroup };
}

interface UpdateDriveItemListDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM_LIST_DISPLAY_TYPE;
	displayType: DriveItemListDisplayType;
}

interface RequestAllDriveItems extends Action {
	type: DriveItemListActionKeys.REQUEST_ALL;
}

interface ReceiveAllDriveItems extends Action {
	type: DriveItemListActionKeys.RECEIVE_ALL;
}

export type DriveItemListAction =
	| RequestDriveItemMetaAction
	| ReceiveDriveItemMetaAction
	| FetchErrorDriveItemMetaAction
	| RequestContentBlockAction
	| ReceiveContentBlockAction
	| FetchErrorContentBlockAction
	| ReceiveDriveItemsAction
	| ReceiveResolutionsAction
	| UpdateDriveItemListDisplayTypeAction
	| RequestAllDriveItems
	| ReceiveAllDriveItems;

export enum DriveItemOrigin {
	List = 'list',
	Details = 'details',
	Menu = 'menu'
}

export const fetchDriveItem = (driveItemId: number, driveFolderId?: number) =>
									(dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { driveList } = getState();

	if (!driveList.driveItems[driveItemId]) {
		if (driveFolderId && driveList.driveItems[driveFolderId]) {
			const driveFolder = driveList.driveItems[driveFolderId];
			const resource = driveFolder.resources ? driveFolder.resources.filter(resource => resource.id === driveItemId)[0] : undefined;

			if (resource) {
				return dispatch(fetchContentBlock(driveFolderId, resource.block));
			}
		}
		return dispatch(getOrFetchDriveItemMeta(driveItemId));
	}
	return Promise.resolve(driveList.driveItems[driveItemId]);
};

let getAllDriveItemsPromise: Promise<number[]>;

export const getAllDriveItems = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	const { driveList, galleryInfos } = getState();

	if (driveList.fetchAllFetchStatus === FetchStatus.NOT_FETCHED) {
		dispatch({ type: DriveItemListActionKeys.REQUEST_ALL });

		const { applicationGallery } = galleryInfos.config;
		const getChildrenPromise = ApiUtils.getChildren();

		if (getChildrenPromise) {
			getAllDriveItemsPromise = getChildrenPromise.then(outcoming => {
				if (outcoming.outcomings && outcoming.resource) {
					let folderIds = outcoming.outcomings.filter(outcoming => outcoming.type === Api.OutcomingType.Directory || outcoming.type === Api.OutcomingType.Gallery);
					folderIds.push(outcoming.resource);
					return Promise.all(folderIds.map(outcoming => dispatch(fetchEntireFolder(outcoming.id))));
				}
				return {};
			})
			.then(() => {
				dispatch({ type: DriveItemListActionKeys.RECEIVE_ALL });

				const { driveList } = getState();
				return Object.keys(driveList.driveItems)
					.map(id => parseInt(id, 10))
					.filter(id => id !== applicationGallery);
			});
		} else {
			return Promise.resolve([]);
		}
	}
	return getAllDriveItemsPromise;
};

const fetchEntireFolder = (driveFolderId: number) =>
									(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	return dispatch(getOrFetchDriveItemMeta(driveFolderId))
		.then(() => {
			const { driveList } = getState();
			const driveFolder = driveList.driveItems[driveFolderId];
			let promises: Promise<{}>[] = [];

			if (driveFolder && driveFolder.blocksNumber) {
				for (let i = 1; i <= driveFolder.blocksNumber; i++) {
					promises.push(dispatch(fetchContentBlock(driveFolderId, i)));
				}
			}
			return Promise.all(promises);
		});
};

const canMetaBeFetched = (driveList: DriveItemFetchState): boolean => driveList.metaFetchStatus === FetchStatus.NOT_FETCHED;

export const getOrFetchDriveItemMeta = (driveItemId: number, isInGallery: boolean = true, isAsync: boolean = true) =>
									(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | Api.DriveItem> => {
	const { driveList } = getState();
	const driveItemList = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveItemId);
	const driveItemMeta = driveList.driveItems[driveItemId];
	const driveItemFetchStatus = driveList.driveItemsFetchStatus[driveItemId];

	if (driveItemMeta && driveItemFetchStatus && driveItemFetchStatus.metaFetchStatus === FetchStatus.FETCHED) {
		return Promise.resolve(driveList.driveItems[driveItemId]);
	}

	if (canMetaBeFetched(driveItemList)) {
		dispatch(requestDriveItemMeta(driveItemId));

		return ApiUtils.getMeta(driveItemId, isAsync)
			.then(driveItem => {
				if (driveItem.resolutions) {
					dispatch(receiveResolutions(driveItem.resolutions));
				}
				dispatch(receiveDriveItems({ [driveItem.id]: driveItem }, isInGallery));
				dispatch(receiveDriveItemMeta(driveItemId));
				return driveItem;
			})
			.catch(error => {
				dispatch(fetchErrorDriveItemMeta(driveItemId));
				dispatch(createNotification('error.fetch_content_error', { id: driveItemId }));
				return Promise.reject({});
			});
	}

	const resolvedDriveItem = driveList.driveItems[driveItemId];
	return Promise.resolve(resolvedDriveItem || {});
};

const requestDriveItemMeta = (driveItemId: number): RequestDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.REQUEST_DRIVE_META
});

const receiveDriveItemMeta = (driveItemId: number): ReceiveDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META
});

const fetchErrorDriveItemMeta = (driveItemId: number): FetchErrorDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META
});

const canDriveListBeFetched = (driveList: DriveItemFetchState, block?: number): boolean => {
	return block !== undefined && (!driveList.contentBlocksStatus[block] || driveList.contentBlocksStatus[block] === FetchStatus.NOT_FETCHED);
};

const fetchContentBlock = (driveFolderId: number, block: number = 1) =>
								(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { driveList, galleryInfos } = getState();
	const galleryId = galleryInfos.config.applicationGallery;
	const driveFolder = driveList.driveItems[driveFolderId];
	const driveItemList = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveFolderId);

	if (canDriveListBeFetched(driveItemList, block) && driveFolder) {
		const { resolutionsBlock } = driveFolder;

		// If the first block is not already loaded, we load it, because it contains the resolution
		if (block !== resolutionsBlock && canDriveListBeFetched(driveItemList, resolutionsBlock)) {
			dispatch(fetchContentBlock(driveFolderId, resolutionsBlock));
		}

		dispatch(requestContentBlock(driveFolderId, block));

		return ApiUtils.getContentBlock(driveFolderId, block)
			.then((json: Api.DriveItemContentBlock) => {
				if (json.resolutions) {
					// if a subfolder has custom resolution for default, they must not be added to the gallery one
					if (driveFolderId !== galleryId) {
						delete json.resolutions.default;
					}
					dispatch(receiveResolutions(json.resolutions));
				}
				dispatch(receiveDriveItems(json.metas));
				return dispatch(receiveContentBlock(driveFolderId, json, block));
			})
			.catch(error => {
				dispatch(fetchErrorContentBlock(driveFolderId, block));
				dispatch(createNotification('error.fetch_content_block_error', { id: driveFolderId }));
				return {};
			});
	}
	return Promise.resolve({});
};

const requestContentBlock = (driveFolderId: number, block: number): RequestContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK,
	block
});

const receiveContentBlock = (driveFolderId: number, json: Api.DriveItemContentBlock, block: number): ReceiveContentBlockAction => {
	const { metas } = json;
	let driveItems: Api.DriveItem[] = [];
	Object.keys(metas).forEach(key => driveItems.push(metas[key]));

	return ({
		driveFolderId,
		type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK,
		block
	});
};

const fetchErrorContentBlock = (driveFolderId: number, block: number): FetchErrorContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK,
	block
});

export const receiveDriveItems = (driveItems: { [k: number]: Api.DriveItem }, isInGallery: boolean = true) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
		const { multiAppAuthenticationInfos, appInfos } = getState();
		const { credentials } = multiAppAuthenticationInfos;
		const { type } = appInfos;
		const resolvedIsInGallery = !UserAgentUtils.isMobileApp ? undefined : isInGallery;

		let multiAppLogin = '';

		if (type === AppType.Multiple && credentials && credentials.login) {
			multiAppLogin = credentials.login;
		}

		dispatch({
			type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS,
			driveItems,
			isInGallery: resolvedIsInGallery,
			multiAppLogin
		});
};

export const receiveResolutions = (resolutions: { [k: string]: Api.ResolutionGroup }): ReceiveResolutionsAction => ({
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS,
	resolutions
});

export const updateDriveItemListDisplayType = (displayType: DriveItemListDisplayType) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	LocalStorageManager.setDisplayType(displayType);
	dispatch({
		type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM_LIST_DISPLAY_TYPE,
		displayType
	});
};

export interface PublicationOptions {
	page?: number;
	hideWelcomePage?: boolean;
}

export const viewDriveItem = (driveItem: Api.DriveItem, origin: DriveItemOrigin, publicationOptions?: PublicationOptions) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

	if (!driveItem) {
		dispatch(createNotification('error.open_fail'));
		return;
	}

	if (driveItem.endDate && Utils.parseDate(driveItem.endDate) < new Date()) {
		dispatch(createNotification('error.content_expired', { id: driveItem.id }));
		return;
	}

	let url: string;
	const { galleryInfos, routerParamsHistory, downloadManager, driveList } = getState();
	const galleryConfig = galleryInfos.config;
	const downloadMode = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id].mode : undefined;
	const isAndroidApp = UserAgentUtils.isAndroidApp();

	switch (driveItem.type) {
		case Api.DriveItemType.Directory:
			window.location.hash = `#${RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, driveItem.id.toString())}`;
			break;
		case Api.DriveItemType.Image:
		case Api.DriveItemType.Movie:
		case Api.DriveItemType.Sound:
			goToTheaterPage(driveItem, routerParamsHistory, galleryConfig, driveList, origin);
			break;
		case Api.DriveItemType.Publication:
			const {
				page = undefined,
				hideWelcomePage = undefined
			} = publicationOptions || {};
			url = UrlUtils.getPublicationUrl(galleryConfig, driveItem, page, UserAgentUtils.isMobileApp, false, hideWelcomePage);
			if (UserAgentUtils.isMobileApp) {
				if (DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id)) {
					const downloadFolderPath = Utils.getPersistentDownloadsFolder();
					const publicationPath = `${driveItem.id}/index.html`;
					const publicationUrlParams = UrlUtils.getPublicationUrlParams(page, UserAgentUtils.isMobileApp, hideWelcomePage);
					const localUrl = `${downloadFolderPath}/${publicationPath}${publicationUrlParams}`;
					NativeApi.openFrame(localUrl);
				} else {
					NativeApi.openFrame(url);
				}
			} else {
				window.open(url);
			}
			break;
		case Api.DriveItemType.File:
			const fileName = DriveItemUtils.getDownloadedFileName(driveItem);
			const isItemPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
			const isItemDownloaded = isItemPersistentlyDownloaded || downloadMode === DownloadMode.Dependency; // Todo better name ?
			if (isItemDownloaded) {
				const downloadFolderPath = UserAgentUtils.isWindowsApp() ?
					Utils.getRelativeDownloadsFolder() :
					Utils.getPersistentDownloadsFolder()
				;
				url = `${downloadFolderPath}/${fileName}`;
			} else if (downloadMode === DownloadMode.Temp) {
				url = `${Utils.getTemporaryDownloadsFolder()}/${fileName}`;
			} else {
				url = UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
			}

			if (isAndroidApp && !downloadMode) {
				// Temporarily downloading the file before opening it in a third party app, to prevent Android crashes on recent versions
				dispatch(requestMobileAppDownload(driveItem, DownloadMode.Temp))
					.then(() => window.open(`${Utils.getTemporaryDownloadsFolder()}/${fileName}`));
			} else {
				if (UserAgentUtils.isWindowsApp() && isItemDownloaded) {
					NativeApi.openFileNatively(url);
				} else {
					window.open(url);
				}
			}
			break;
		case Api.DriveItemType.ExternalLink:
			window.open(driveItem.url, '_blank');
			break;
		case Api.DriveItemType.Component:
			window.location.hash = `#${RoutesUtils.getPathStaticPage(driveItem.id.toString())}`;
			break;
		default:
			// Types to ignore Template, V2Gateway, Link, AppContainer, Gallery
			break;
	}
};

const goToTheaterPage = (
	driveItem: Api.DriveItem,
	routerParamsHistory: RouterParamsState[],
	galleryConfig: Api.Config,
	driveList: DriveListState,
	source: DriveItemOrigin
): void => {
	const currentRoute = routerParamsHistory[routerParamsHistory.length - 1];
	const { params } = currentRoute;
	let origin;
	let originParam;

	if (source === DriveItemOrigin.List) {
		origin = (params.origin as DriveItemListOrigin) || DriveItemListOrigin.Folder;
		switch (origin) {
			case DriveItemListOrigin.Folder:
				originParam = params.originParam || galleryConfig.applicationGallery;
				break;
			case DriveItemListOrigin.Search:
			case DriveItemListOrigin.Selection:
				originParam = params.originParam;
				break;
		}
	} else if (source === DriveItemOrigin.Details) {
		origin = params.origin as DriveItemListOrigin || DriveItemListOrigin.Folder;
		originParam = params.folderId || resolveParentId(driveItem.id, driveList);
	} else {
		origin = DriveItemOrigin.Menu;
	}

	window.location.hash = `#${RoutesUtils.getPathTheater(origin, originParam, driveItem.id)}`;
};

export const resolveParentId = (driveItemId: number, driveList: DriveListState): number | void => {
	const { driveItems } = driveList;
	const driveItemsKeys = Object.keys(driveItems);

	for (let i = 0; i < driveItemsKeys.length; i++) {
		const folderId = parseInt(driveItemsKeys[i], 10);
		const folderMeta = driveItems[folderId];

		if (folderMeta && folderMeta.resources) {
			for (let j = 0; j < folderMeta.resources.length; j++) {
				const resource = folderMeta.resources[j];

				if (resource.id === driveItemId) {
					return folderId;
				}
			}
		}
	}
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/actions.ts